import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { compose, withContext } from 'recompose';

import PageError from '../components/page-error/page-error';
import {
  sendAppLoadFinishBiEvent,
  sendAppLoadStartBiEvent,
} from '../redux/actions/app-loading';
import { StoreState } from '../redux/reducers/store.types';
import { RouterContext, RouterState } from '../types/router';
import { navigation } from '../utils';

import { sendPageLoadBiEvent, show } from './router.actions';
import { getRouter } from './router.selectors';

type RouteChildrenFN = (routerContext: RouterContext) => React.ReactNode;

interface StateProps {
  router: RouterState;
}

interface DispatchProps {
  onEnter(path: string, context: RouterContext, error?: any): void;
  sendAppLoadStartBiEvent(params: {
    projectId: string;
    organizationId: string;
  }): void;
  sendAppLoadFinishBiEvent(params: {
    projectId: string;
    organizationId: string;
    loadingTime: number;
  }): void;
  sendPageLoadBiEvent(): void;
}

interface OwnProps {
  path: string;
  beforeEnter?(callback: () => void): void;
  layout?: React.FunctionComponent<any> | React.ComponentClass<any> | string;
  component?(): Promise<any>;
  children?: React.ReactElement | RouteChildrenFN | null;
}

type Props = DispatchProps & OwnProps & StateProps;

interface DefaultProps {
  beforeEnter(callback: () => void): void;
  component(): Promise<any>;
}

type OuterProps = OwnProps;

class RouteComponent extends React.PureComponent<Props> {
  public static defaultProps: DefaultProps = {
    // by default all routes allowed
    // and callback calls immediately
    beforeEnter: (callback) => callback(),
    component: () => Promise.resolve(),
  };

  public static startLoadTime: number;
  public static loadingTime: number;

  public id: string = '';
  public lazyComponent: any;

  constructor(props) {
    super(props);
    const { path } = props;

    this.id = path;
    navigation.goToPage(path, this.handleNavigation.bind(this));
  }

  public handleNavigation(context) {
    const {
      component,
      beforeEnter,
      onEnter,
      sendAppLoadStartBiEvent,
      sendPageLoadBiEvent,
      sendAppLoadFinishBiEvent,
    } = this.props;

    if (!RouteComponent.startLoadTime) {
      RouteComponent.startLoadTime = window.performance.now();

      sendAppLoadStartBiEvent({
        organizationId: context.params.organizationId,
        projectId: context.params.projectId,
      });
    }

    if (!beforeEnter) {
      return;
    }

    // check if route allowed
    beforeEnter(() => {
      if (!component) {
        return;
      }

      component().then((Component) => {
        this.lazyComponent = Component;

        if (navigation.isActivePath(this.id)) {
          onEnter(this.id, context);
          sendPageLoadBiEvent();

          if (!RouteComponent.loadingTime) {
            sendAppLoadFinishBiEvent({
              organizationId: context.params.organizationId,
              projectId: context.params.projectId,
              loadingTime: RouteComponent.getLoadingTime(),
            });
          }
        }
      });
    });
  }

  public render() {
    const { router, layout, children } = this.props;
    const routerContext = router.context;
    const childrenProps = { router: routerContext };

    if (router.path !== this.id) {
      return null;
    }
    if (router.error) {
      return <PageError />;
    }

    const childrenResult = () => {
      if (_.isFunction(children)) {
        return children(routerContext);
      }
      if (children) {
        return React.cloneElement(children, childrenProps);
      }
      if (this.lazyComponent) {
        return React.createElement(this.lazyComponent, childrenProps);
      }
      return null;
    };

    if (!layout) {
      return null;
    }
    return React.createElement(layout, { children: childrenResult() });
  }

  public static getLoadingTime(): number {
    return (RouteComponent.loadingTime = Math.floor(
      window.performance.now() - RouteComponent.startLoadTime,
    ));
  }
}

const mapStateToProps = (state: StoreState) => ({
  router: getRouter(state),
});

const mapDispatchToProps = (dispatch) => ({
  onEnter: (path: string, routerContext: RouterContext, error) => {
    dispatch(show(path, routerContext, error));
  },
  sendAppLoadStartBiEvent: ({
    projectId,
    organizationId,
  }: {
    projectId: string;
    organizationId: string;
  }) =>
    dispatch(
      sendAppLoadStartBiEvent({
        projectId,
        organizationId,
      }),
    ),
  sendAppLoadFinishBiEvent: ({
    projectId,
    organizationId,
    loadingTime,
  }: {
    projectId: string;
    organizationId: string;
    loadingTime: number;
  }) =>
    dispatch(
      sendAppLoadFinishBiEvent({
        projectId,
        organizationId,
        loadingTime,
      }),
    ),
  sendPageLoadBiEvent: () => dispatch(sendPageLoadBiEvent()),
});

export const Route = compose<Props, OuterProps>(
  connect<StateProps, DispatchProps, OwnProps>(
    mapStateToProps,
    mapDispatchToProps,
    (stateProps, dispatchProps, ownProps) => {
      return {
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
      };
    },
  ),
  withContext({ routerContext: () => null }, ({ routerContext }) => ({
    routerContext,
  })),
)(RouteComponent);
